import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { v4 as uuidgen } from 'uuid';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../../../../models';
import { useErrorMessages, useTextMessaging } from '../../../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../../../models/Logging';
import { getDisplayName } from '../../../../../../../../models/NameUtils';
import { getFormattedPhoneNumber } from '../../../../../../../../models/UiUtils';
import { useConversationMessagesInfiniteQuery } from '../../../../../../../../queries/Conversation/useConversationMessagesInfiniteQuery';
import { DeprecatedCloseButton } from '../../../../../../../components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../../../../../components/LoadingSpinner';
import { Modal } from '../../../../../../../components/Modal';
import { MessageBubble } from '../../../../../../../components/texting/MessageBubble';
import { TextMessageInput } from '../../../../../../../components/texting/TextMessageInput';
import { TextRegistrationInProgressModal } from '../../../../../../../components/texting/TextRegistrationInProgressModal';
import { TextRegistrationRequiredModal } from '../../../../../../../components/texting/TextRegistrationRequiredModal';
import { useTextingCampaignRegistrationContext } from '../../../../../../../containers/Texting/TextingCampaignRegistrationPrompt/context';
import { baseStyleSheet } from '../../../../../../../styles/styles';
import { styleSheet } from './styles';

interface IProps {
	isOpen: boolean;
	onRequestClose: () => void;
	conversationEvent: Api.ConversationThreadEventViewModel;
	entity?: Api.IEntity | Api.TimelineEventViewModel<Api.ITimelineEvent>;
}

interface IConversationComposerTextMessage extends Partial<Api.ITextMessage> {
	file?: File;
	pendingId?: string;
}

const ConversationThreadModalBase = ({
	isOpen,
	onRequestClose,
	conversationEvent,
	entity,
}: IProps & IModalContext<boolean>) => {
	const logger = useEventLogging('ConversationThreadModal');
	const errorMessages = useErrorMessages();
	const textMessagingVm = useTextMessaging();
	const [pendingTextMessages, setPendingTextMessages] = useState<IConversationComposerTextMessage[]>(null);
	const [conversation, setConversation] = useState<Api.ConversationViewModel>(null);
	const shouldRemoveConversationOnCloseRef = useRef(false);

	const conversationId = conversationEvent?.resourceId;

	// Use infinite query for message fetching
	const messagesQuery = useConversationMessagesInfiniteQuery({
		conversationId,
		enabled: isOpen && !!conversationId,
	});

	const {
		showRegistrationRequiredModal,
		isCampaignRegistrationRequired,
		showRegistrationInProgressModal,
		isRegistrationInProgress,
		setShowRegistrationRequiredModal,
		setShowRegistrationInProgressModal,
		isLoading,
		textSettings,
		checkShowRegistration,
	} = useTextingCampaignRegistrationContext();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(checkShowRegistration, []);

	const didSendMessageRef = useRef(false);

	// Load the conversation for sending messages and mark as read
	useEffect(() => {
		if (!isOpen || !conversationId || conversation) {
			return;
		}

		shouldRemoveConversationOnCloseRef.current = !textMessagingVm.hasConversation(conversationId);
		textMessagingVm
			.getConversation(conversationId)
			.then(convo => {
				setConversation(convo);
				// Mark conversation as read
				convo.markAsRead().catch(err => {
					logger.logApiError('ConversationMarkAsRead-Error', err);
					errorMessages.pushApiError(err);
				});
			})
			.catch(err => {
				logger.logApiError('LoadConversationInModal-Error', err);
				errorMessages.pushApiError(err);
			});

		return () => {
			if (!!conversation && shouldRemoveConversationOnCloseRef.current) {
				textMessagingVm.removeConversation(conversation);
			}
		};
	}, [conversation, isOpen, conversationId, textMessagingVm, logger, errorMessages]);

	// Extract and flatten messages from all query pages
	const messages = useMemo(() => {
		if (!messagesQuery.data) {
			return [];
		}

		return messagesQuery.data.pages
			.flatMap(page => page.values)
			.filter(message => message.media?.length || message.text)
			.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());
	}, [messagesQuery.data]);

	const renderPlaceholderMessages = () => {
		const pendingHasAttachment = !!pendingTextMessages?.find(x => x.file);
		return pendingTextMessages?.map((textMessage, i, all) => {
			return (
				<MessageBubble
					className='message'
					id={textMessage.pendingId}
					key={`${textMessage.pendingId}-${i}`}
					showStatus={!pendingHasAttachment || i === all.length - 1}
					textMessage={textMessage}
					entity={entity}
				/>
			);
		});
	};

	const renderMessages = () => {
		if (!messages?.length && !pendingTextMessages?.length) {
			return <div className={css(styleSheet.noMessages)}>No messages found</div>;
		}

		// Combine actual messages with placeholder messages
		const placeholderMessages = renderPlaceholderMessages() || [];

		// Add date separators
		const dateSepsAdded = new Set();
		const today = moment();
		const yesterday = today.clone().subtract(1, 'day');

		const messagesWithSeps: JSX.Element[] = [];

		messages.forEach((message, i, arr) => {
			const messageDate = moment(message.creationDate);

			// Handle media messages
			if (message.media?.length) {
				message.media.forEach((m, ii) => {
					messagesWithSeps.push(
						<MessageBubble
							key={`${message.id}-media-${ii}`}
							textMessage={message}
							id={`${message.id}-media-${ii}`}
							showAvatar={true}
							showTimeStamp={true}
							mediaContent={m}
							entity={entity}
						/>
					);
				});
			}

			// Handle text messages
			if (message.text) {
				messagesWithSeps.push(
					<MessageBubble
						key={message.id}
						textMessage={message}
						id={message.id}
						showAvatar={true}
						showTimeStamp={true}
						entity={entity}
					/>
				);
			}

			// Show date separator after messages (due to column-reverse display)
			// Add separator when transitioning to next date or at end of messages
			if (i === messages.length - 1 || !messageDate.isSame(moment(arr[i + 1]?.creationDate), 'day')) {
				const text = messageDate.isSame(today, 'day')
					? 'Today'
					: messageDate.isSame(yesterday, 'day')
						? 'Yesterday'
						: messageDate.format('MMM Do, yyyy');

				if (!dateSepsAdded.has(text)) {
					dateSepsAdded.add(text);
					const dateSep = (
						<div key={`date-${text}-${i}`} className={css(styleSheet.dateSeparator)}>
							<span>{text}</span>
						</div>
					);
					messagesWithSeps.push(dateSep);
				}
			}
		});

		// Add placeholder messages at the end
		return [...messagesWithSeps, ...placeholderMessages];
	};

	const sendTextMessage = useCallback(
		async (msg: string, attachments: Api.AttachmentsToBeUploadedViewModel<File>) => {
			const pendingMessages: IConversationComposerTextMessage[] = [];
			if (msg) {
				const textMessage: IConversationComposerTextMessage = {
					direction: Api.Direction.Outbound,
					pendingId: uuidgen(),
					status: Api.TextStatus.Sending,
					text: msg,
				};
				pendingMessages.push(textMessage);
			}

			if (attachments) {
				attachments.attachments.forEach(file => {
					const textMessage: IConversationComposerTextMessage = {
						direction: Api.Direction.Outbound,
						file,
						pendingId: uuidgen(),
						status: Api.TextStatus.Sending,
					};
					pendingMessages.push(textMessage);
				});
			}

			try {
				setPendingTextMessages(pendingMessages);
				await conversation?.sendMessage(msg, attachments);

				setPendingTextMessages(null);
				didSendMessageRef.current = true;
			} catch (err) {
				logger.logApiError('SendTextMessage-Error', err);
				errorMessages.pushApiError(Api.asApiError(err));
			}
		},
		[conversation, logger, errorMessages]
	);

	const handleClose = () => {
		// Do not remove if there was a message sent.
		if (shouldRemoveConversationOnCloseRef.current && didSendMessageRef.current) {
			shouldRemoveConversationOnCloseRef.current = false;
		}
		onRequestClose();
	};

	const onCloseRegistrationWarningClicked = () => {
		setShowRegistrationRequiredModal(false);
	};

	const onCloseRegistrationInProgressClicked = () => {
		setShowRegistrationInProgressModal(false);
	};

	// Extract recipient info from the loaded conversation (like the old component)
	const recipient = conversation?.toNumbers?.[0];

	if (isLoading || messagesQuery.isLoading) {
		return (
			<Modal
				isOpen={isOpen}
				onRequestClose={onRequestClose}
				shouldCloseOnOverlayClick={false}
				className={css(styleSheet.modalClassName)}
			>
				<div className={css(styleSheet.entityConversationComposer, styleSheet.loadingSpinnerContainer)}>
					<LoadingSpinner type='small' />
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleClose}
			shouldCloseOnOverlayClick={false}
			className={css(styleSheet.modalClassName)}
		>
			<div className={css(styleSheet.entityConversationComposer)}>
				<div className={css(styleSheet.entityConversationComposerConversationHeader)}>
					<DeprecatedCloseButton onClick={handleClose} />
				</div>
				<div
					className={css(baseStyleSheet.verticalStack, styleSheet.entityConversationComposerConversationContactHeader)}
				>
					<div>{getDisplayName(recipient?.contact)}</div>
					{recipient?.number?.e164 && <div>{`(${getFormattedPhoneNumber(recipient.number.e164)})`}</div>}
				</div>
				<div className={css(styleSheet.entityConversationComposerConversation)}>
					<div className={css(styleSheet.conversationMessagesContainer)}>
						{renderMessages()}
						{messagesQuery.hasNextPage && !messagesQuery.isFetchingNextPage && (
							<Waypoint topOffset='-200px' onEnter={() => messagesQuery.fetchNextPage()} />
						)}
						{messagesQuery.isFetchingNextPage && <LoadingSpinner type='small' />}
					</div>
					<TextMessageInput
						className='text-message-input'
						conversation={conversation}
						onSend={sendTextMessage}
						hideTextSend={isCampaignRegistrationRequired || isRegistrationInProgress}
					/>
				</div>
				<TextRegistrationRequiredModal
					showRegistrationRequiredModal={showRegistrationRequiredModal}
					telephonyTextSettings={textSettings}
					onRequestClose={onCloseRegistrationWarningClicked}
				/>
				<TextRegistrationInProgressModal
					showRegistrationRequiredModal={showRegistrationInProgressModal}
					onRequestClose={onCloseRegistrationInProgressClicked}
				/>
			</div>
		</Modal>
	);
};

const ConversationThreadModalWithContext = inject(ModalChildComponentContextKey)(observer(ConversationThreadModalBase));

export const ConversationThreadModal = ({ isOpen, onRequestClose, conversationEvent, entity }: IProps) => {
	return (
		<ConversationThreadModalWithContext
			isOpen={isOpen}
			onRequestClose={onRequestClose}
			conversationEvent={conversationEvent}
			entity={entity}
			parentModal={{ onRequestClose }}
		/>
	);
};
